import { AxiosError } from 'axios'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMatch } from 'react-router'
import { Link } from 'react-router-dom'
import { ErrorsContext } from '../contexts/errors_context'
import { SearchContext } from '../contexts/search_context'
import { applyDocumentMeta, cardPageMetaFromItem } from '../lib/seo_meta'
import { itemsRepository } from '../repositories/items_repository'

import '../stylesheets/Sets.scss'
import { cardBreadcrumbTags, cardPageJsonLd, localizedName } from '../lib/json_ld'
import { Item, Tag, Unit } from '../types'
import { CardCatalogFacts } from './card_catalog_facts'
import { CardSingle } from './card_single'
import { GenericNotFound } from './generic_not_found'
import { JsonLd } from './json_ld'
import { ListingSingle } from './listing_single'

const cardSettingsItem = {
  conditionSelection: true,
  canCollect: true,
  imageResolution: '2x',
}

const Items = (): JSX.Element => {
  const { i18n, t } = useTranslation()

  const { filters, searchString, endpointForExistingFilters } = useContext(SearchContext)

  const { addError } = useContext(ErrorsContext)
  const [loading, setLoading] = useState<boolean>(true)
  const [item, setItem] = useState<Item>()
  const [listingsLoading, setListingsLoading] = useState<boolean>(false)
  const [listingState, setListingState] = useState<{ itemId: number, units: Unit[] }>()

  const params = useMatch('/categories/:category_id/items/:item_id')?.params

  // const categoryId = useMemo<number|undefined>(() => {
  //   const id = parseInt(params?.category_id ?? '')
  //   return isNaN(id) ? undefined : id
  // }, [params])

  const itemId = useMemo<number|undefined>(() => {
    const id = parseInt(params?.item_id ?? '')
    return isNaN(id) ? undefined : id
  }, [params])

  useEffect(() => {
    if (!itemId) {
      setLoading(false)
      return
    }

    let active = true
    setLoading(true)

    itemsRepository
      .get(itemId)
      .then(({ item: next }) => {
        if (!active) return
        setItem(next)
        setListingsLoading(true)
      })
      .catch((err: AxiosError) => {
        if (!active) return
        addError?.(err)
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
    }
  }, [addError, itemId])

  useEffect(() => {
    if (!item) return

    const requestedId = item.id
    let active = true
    setListingsLoading(true)

    itemsRepository
      .units(requestedId, '')
      .then(({ listings }) => {
        if (!active) return
        setListingState({ itemId: requestedId, units: listings })
      })
      .catch((err: AxiosError) => {
        if (!active) return
        addError?.(err)
      })
      .finally(() => {
        if (active) setListingsLoading(false)
      })

    return () => {
      active = false
    }
  }, [addError, item])

  const units = listingState && item && listingState.itemId === item.id ? listingState.units : undefined

  // determine category
  const category = useMemo<Tag|undefined>(() => {
    if (!item || item.tags.length === 0) return

    const categoryTags = item.tags.filter(t => t.type === 'Category')

    if (categoryTags.length === 0) return

    return categoryTags.reduce((prev, current) => prev.depth > current.depth ? prev : current)
  }, [item])

  const categoriesLink = useCallback((tagId: string) => {
    if (!endpointForExistingFilters) return ''

    const p = `/categories/${tagId}`

    const params = endpointForExistingFilters(filters, searchString)

    return p + params
  }, [endpointForExistingFilters, filters, searchString])

  const categoryCrumbs = useMemo(() => {
    return cardBreadcrumbTags(category).map(tag => ({
      id: tag.id,
      name: localizedName(tag, i18n.language),
      path: `/categories/${tag.id}`,
    }))
  }, [category, i18n.language])

  const breadcrumbs = useMemo(() => {
    if (categoryCrumbs.length === 0) return

    return (
      <p className='breadcrumbs'>
        {
          categoryCrumbs.map(crumb => (
            <Link key={ crumb.id } to={ categoriesLink(String(crumb.id)) }>{ crumb.name }</Link>
          ))
        }
      </p>
    )
  }, [categoriesLink, categoryCrumbs])

  const itemName = useMemo(() => {
    if (!item) return

    return localizedName(item, i18n.language)
  }, [i18n.language, item])

  const routeCategoryId = useMemo(() => {
    const id = parseInt(params?.category_id ?? '')
    return isNaN(id) ? undefined : id
  }, [params?.category_id])

  const jsonLd = useMemo(() => {
    if (!item || !itemName) return null

    return cardPageJsonLd({
      language: i18n.language,
      item,
      categoryId: routeCategoryId,
      units,
      conditionLabel: (condition: number) => t(`collections.conditions.long.${condition}`),
    })
  }, [i18n.language, item, itemName, routeCategoryId, t, units])

  useEffect(() => {
    if (!item) return

    applyDocumentMeta(cardPageMetaFromItem(item))
  }, [item])

  const listings = useMemo(() => {
    if (!item) return

    if (listingsLoading) {
      return <p>{ t('cards.marketplace.loading') }</p>
    }

    if (!units || units.length === 0) {
      return (
        <>
          <p>{ t('cards.marketplace.none.body1') }</p>
          <p><strong>{ t('cards.marketplace.none.body2', { name: itemName }) }</strong></p>
          <p>{ t('cards.marketplace.none.body3') }</p>
        </>
      )
    }

    return (
      <ul className="listings">
        {
          units.map((ui, i) => {
            return <ListingSingle key={ i } editing={ false } items={ [item] } unit={ ui } />
          })
        }
      </ul>
    )
  }, [item, itemName, listingsLoading, t, units])

  if (loading) {
    return <p>{ t('cards.marketplace.loading') }</p>
  }

  if (!item) return <GenericNotFound />

  return (
    <div>
      <JsonLd data={ jsonLd } />
      { breadcrumbs }
      <h2>{ itemName }</h2>
      <div>
        <ul className="cards-list test">
          <CardSingle key={ item.id } item={ item } cardSettings={ cardSettingsItem } />
        </ul>
      </div>
      <CardCatalogFacts item={ item } />
      <hr />
      <div>
        <h2>{ t('cards.marketplace.title') }</h2>
        { listings }
      </div>
    </div>
  )
}

export { Items }
